import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Settings as SettingsIcon,
  Upload,
  User,
  Image as ImageIcon,
  Sparkles,
  Building2,
  Code,
  Copy,
  Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatWidgetSettings() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actor, setActor] = useState(null);
  const [branding, setBranding] = useState(null);
  const [settings, setSettings] = useState({
    user_session_enabled: false,
    use_actor_image: true,
    custom_logo: null,
    animation_enabled: true,
    company_name: "",
    rate_limiting_enabled: true,
    max_messages_per_day: 10
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [embedScript, setEmbedScript] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, [actorId]);

  useEffect(() => {
    if (actor && branding) {
      generateEmbedScript();
    }
  }, [actor, branding, settings]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetch actor details
      const actorsRes = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundActor = actorsRes.data.find(a => a.id === actorId);
      if (!foundActor) {
        toast.error("AI Actor not found");
        navigate("/ai/actors");
        return;
      }
      setActor(foundActor);

      // Fetch branding settings
      const brandingRes = await axios.get(`${API}/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranding(brandingRes.data);

      // Fetch widget settings
      const widgetRes = await axios.get(`${API}/ai/actors/${actorId}/widget-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(widgetRes.data);

      // Set logo preview if custom logo exists
      if (widgetRes.data.custom_logo) {
        // Handle logo URL to ensure /api/uploads/ prefix
        let logoPath = widgetRes.data.custom_logo;
        if (logoPath.startsWith('/uploads/') && !logoPath.startsWith('/api/uploads/')) {
          logoPath = '/api' + logoPath;
        }
        setLogoPreview(`${BACKEND_URL}${logoPath}`);
        console.log('Logo preview set to:', `${BACKEND_URL}${logoPath}`);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("Please select a logo file first");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("logo", logoFile);

      const response = await axios.post(
        `${API}/ai/actors/${actorId}/widget-settings/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const logoUrl = response.data.logo_url;
      setSettings({ ...settings, custom_logo: logoUrl });
      setLogoPreview(`${BACKEND_URL}${logoUrl}`);
      toast.success("Logo uploaded successfully");
      setLogoFile(null);
      console.log('Logo uploaded and preview updated:', `${BACKEND_URL}${logoUrl}`);
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/ai/actors/${actorId}/widget-settings`,
        {
          actor_id: actorId,
          ...settings
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success("Widget settings saved successfully");
      await fetchData();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const generateEmbedScript = () => {
    const brandName = branding?.brand_name || "AI Assistant";
    const primaryColor = branding?.primary_color || "#4F46E5";
    const secondaryColor = branding?.secondary_color || "#2563EB";

    let logoUrl = '';
    if (settings.use_actor_image && actor?.profile_image) {
      logoUrl = BACKEND_URL + actor.profile_image;
    } else if (!settings.use_actor_image && settings.custom_logo) {
      let logoPath = settings.custom_logo;
      if (logoPath.startsWith('/uploads/') && !logoPath.startsWith('/api/uploads/')) {
        logoUrl = BACKEND_URL + '/api' + logoPath;
      } else {
        logoUrl = BACKEND_URL + logoPath;
      }
    }

    const script = `<!--Start of ${brandName} AI Chat Widget-->
<script type="text/javascript">
(function() {
  var config = {
    actorId: '${actorId}',
    actorName: '${actor?.name || 'AI Assistant'}',
    apiUrl: '${BACKEND_URL}/api',
    brandName: '${brandName}',
    primaryColor: '${primaryColor}',
    secondaryColor: '${secondaryColor}',
    logo: '${logoUrl}',
    companyName: '${settings.company_name || ''}',
    animationEnabled: ${settings.animation_enabled},
    userSessionEnabled: ${settings.user_session_enabled}
  };
  
  var script = document.createElement('script');
  script.src = '${BACKEND_URL}/api/widget/chat-widget.js?v=' + Date.now();
  script.async = true;
  script.onload = function() {
    if (window.initAIChatWidget) {
      window.initAIChatWidget(config);
    }
  };
  document.head.appendChild(script);
  
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = '${BACKEND_URL}/api/widget/chat-widget.css?v=' + Date.now();
  document.head.appendChild(link);
})();
</script>
<!--End of ${brandName} AI Chat Widget-->`;

    setEmbedScript(script);
  };

  const copyEmbedScript = () => {
    navigator.clipboard.writeText(embedScript);
    setCopied(true);
    toast.success("Embed script copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Loading widget settings...</p>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ai/actors/view/${actorId}`)}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Actor View
          </Button>
          <h1 className="text-3xl font-bold text-slate-800">Chat Widget Settings</h1>
          <p className="text-slate-600 mt-1">Configure chat widget behavior and appearance for {actor?.name}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <Separator />

      {/* User Session Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            User Session Management
          </CardTitle>
          <CardDescription>
            Control how users authenticate before chatting
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="user-session">Require User Registration (OTP)</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, users must provide name and email, verified via OTP
              </p>
            </div>
            <Switch
              id="user-session"
              checked={settings.user_session_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, user_session_enabled: checked })
              }
            />
          </div>

          {!settings.user_session_enabled && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              <strong>Note:</strong> When disabled, pseudo sessions are created automatically. 
              In Chat Moderation, you'll see pseudo names and emails (e.g., Guest_abc123@pseudo.com).
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo/Logo Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-purple-600" />
            Widget Photo/Logo
          </CardTitle>
          <CardDescription>
            Choose between AI Actor profile image or custom logo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use-actor-image">Use AI Actor Profile Image</Label>
              <p className="text-sm text-muted-foreground">
                Toggle off to upload a custom logo instead
                {settings.custom_logo && settings.use_actor_image && (
                  <span className="ml-2 text-green-600 font-medium">✓ Custom logo uploaded</span>
                )}
              </p>
            </div>
            <Switch
              id="use-actor-image"
              checked={settings.use_actor_image}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, use_actor_image: checked })
              }
            />
          </div>

          {!settings.use_actor_image && (
            <>
              {logoPreview && (
                <div className="border rounded-lg p-4 bg-slate-50 flex items-center justify-center">
                  <img
                    src={logoPreview}
                    alt="Custom Logo Preview"
                    className="max-h-32 object-contain"
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />
                  <p className="text-xs text-slate-500 mt-1">Max size: 5MB</p>
                </div>
                {logoFile && (
                  <Button
                    type="button"
                    onClick={handleLogoUpload}
                    disabled={uploading}
                    variant="outline"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Animation Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-600" />
            Chat Button Animation
          </CardTitle>
          <CardDescription>
            Control the pulse animation on the chat button
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="animation">Enable Pulse Animation</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, the chat button will have a pulsing effect to attract attention
              </p>
            </div>
            <Switch
              id="animation"
              checked={settings.animation_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, animation_enabled: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Company Name Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Company Name
          </CardTitle>
          <CardDescription>
            Display company name above the AI Actor name in the widget header
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name (Optional)</Label>
            <Input
              id="company-name"
              type="text"
              placeholder="Enter company name"
              value={settings.company_name || ""}
              onChange={(e) =>
                setSettings({ ...settings, company_name: e.target.value })
              }
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              This will appear above "{actor?.name}" in the chat widget header
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limiting Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-orange-600" />
            Rate Limiting
          </CardTitle>
          <CardDescription>
            Control how many messages users can send per day
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="rate-limiting">Enable Rate Limiting</Label>
              <p className="text-xs text-muted-foreground">
                Limit the number of messages users can send per day
              </p>
            </div>
            <Switch
              id="rate-limiting"
              checked={settings.rate_limiting_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, rate_limiting_enabled: checked })
              }
            />
          </div>

          {/* Max Messages Input - Only shown if rate limiting is enabled */}
          {settings.rate_limiting_enabled && (
            <div className="space-y-2 pt-2 border-t">
              <Label htmlFor="max-messages">Maximum Messages Per Day</Label>
              <Input
                id="max-messages"
                type="number"
                min="1"
                max="1000"
                value={settings.max_messages_per_day || 10}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    max_messages_per_day: parseInt(e.target.value) || 10
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Each user (both guest and verified) can send up to this many messages per day
              </p>
            </div>
          )}

          {/* Warning when disabled */}
          {!settings.rate_limiting_enabled && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <span>
                  Warning: Disabling rate limiting allows unlimited messages. This may significantly increase your API costs.
                </span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Embed Script */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-green-600" />
            Website Embed Script
          </CardTitle>
          <CardDescription>
            Copy this script to embed the chat widget on your website with the configured settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-xs font-mono max-h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap">{embedScript}</pre>
            </div>
            <Button
              onClick={copyEmbedScript}
              className="absolute top-2 right-2"
              size="sm"
              variant="secondary"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Script
                </>
              )}
            </Button>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
            <p className="text-sm font-semibold text-blue-900">How to use:</p>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Configure all settings above and click "Save Settings"</li>
              <li>Copy the embed script</li>
              <li>Paste it into your website's HTML before the closing <code className="bg-blue-100 px-1 rounded">&lt;/body&gt;</code> tag</li>
              <li>The chat widget will appear with your configured settings</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
